// Libs
import classNames from 'classnames/bind';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import { BaseTable, Loading } from '~/components/common';
// Others
import { mockDataServiceUnit } from '~/utils/mockData/service';
import { IBaseOption, IHeaderCellType } from '~/utils/interface/common';
import { IMaterialsJob, IServiceJob } from '~/utils/interface/job';
import { IDetailEstimate, IParamsDetailEstimate } from '~/utils/interface/estimate';
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { TypeParamEstimateDetail } from '~/utils/enum/common';
import { formatCurrency, formatDecimal } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getDetailEstimate } from '~/thunks/estimate/estimateThunk';
// Styles, images, icons
import styles from './EstimateLineItems.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const columnsServices = (t: TFunction): IHeaderCellType<IServiceJob>[] => {
  return [
    {
      id: 'name',
      label: t('tab_line_items_services_table_name'),
      render: (record: IServiceJob) => record?.name || EMPTY_STRING,
    },
    {
      id: 'price',
      label: t('tab_line_items_services_table_price'),
      render: (record: IServiceJob) => (record.price ? formatCurrency(Number(record.price)) : EMPTY_STRING),
      width: 150,
    },
    {
      id: 'unit',
      label: t('tab_line_items_services_table_unit'),
      render: (record: IServiceJob) => {
        const unit: IBaseOption = mockDataServiceUnit.find(
          (item) => item.value === record?.unit
        ) as IBaseOption;

        return unit ? unit?.label : EMPTY_STRING;
      },
      width: 150,
    },
    {
      id: 'quantity',
      label: t('tab_line_items_services_table_quantity'),
      render: (record: IServiceJob) =>
        record?.quantity ? formatDecimal(Number(record?.quantity)) : EMPTY_STRING,
      width: 150,
    },
  ];
};

const columnsMaterials = (t: TFunction): IHeaderCellType<IMaterialsJob>[] => {
  return [
    {
      id: 'name',
      label: t('tab_line_items_materials_table_name'),
      render: (record: IMaterialsJob) => record?.name || EMPTY_STRING,
    },
    {
      id: 'price',
      label: t('tab_line_items_materials_table_price'),
      render: (record: IMaterialsJob) => (record.price ? formatCurrency(Number(record.price)) : EMPTY_STRING),
      width: 150,
    },
    {
      id: 'unit',
      label: t('tab_line_items_materials_table_unit'),
      render: (record: IMaterialsJob) => {
        const unit: IBaseOption = mockDataServiceUnit.find(
          (item) => item.value === record?.unit
        ) as IBaseOption;

        return unit ? unit?.label : EMPTY_STRING;
      },
      width: 150,
    },
    {
      id: 'quantity',
      label: t('tab_line_items_materials_table_quantity'),
      render: (record: IMaterialsJob) =>
        record?.quantity ? formatDecimal(Number(record?.quantity)) : EMPTY_STRING,
      width: 150,
    },
  ];
};

const EstimateLineItems = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate']);
  const dispatch = useAppDispatch();
  const { id: estimateId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [estimateDetail, setEstimateDetail] = useState<IDetailEstimate>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  const handleGetEstimateDetails = useCallback((params: IParamsDetailEstimate) => {
    setIsLoading(true);

    dispatch(getDetailEstimate(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        setEstimateDetail(res?.data);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  useEffect(() => {
    if (!estimateId) return;
    const params: IParamsDetailEstimate = {
      estimateId,
      type: TypeParamEstimateDetail.LINE_ITEM,
    };

    handleGetEstimateDetails(params);

  }, [estimateId, handleGetEstimateDetails]);
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Box component={'div'} id='estimateLineItemsPage' className={cx('container')}>
      <Box component={'div'} className={cx('viewBody')}>
        <Box component={'div'} className={cx('body')}>
          <Typography component={'div'} className={cx('title')}>
            {t('tab_line_items_title_services')}
          </Typography>
          <BaseTable columns={columnsServices(t)} dataSource={estimateDetail?.services || []} />
        </Box>

        <Box component={'div'} className={cx('body')}>
          <Typography component={'div'} className={cx('title')}>
            {t('tab_line_items_title_materials')}
          </Typography>
          <BaseTable columns={columnsMaterials(t)} dataSource={estimateDetail?.materials || []} />
        </Box>
      </Box>

      <Stack direction='row' className={cx('viewTotal')}>
        <Box className={cx('item')}>
          <Box className={cx('textTitle')}>{t('tab_line_items_title_subtotal')}</Box>
          <Box className={cx('textSubtotal')}>
            {formatCurrency(estimateDetail?.subTotal || DEFAULT_NUMBER_ZERO)}
          </Box>
        </Box>

        <Box className={cx('item')}>
          <Box className={cx('textTitleTotal')}>{t('tab_line_items_title_total')}</Box>
          <Box className={cx('textSubtotal')}>
            {formatCurrency(estimateDetail?.total || DEFAULT_NUMBER_ZERO)}
          </Box>
        </Box>
      </Stack>
      {isLoading && <Loading />}
    </Box>
  );
};

export default EstimateLineItems;
